import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { db, QueuedRequest, PendingMedia } from '../services/db';
import { api, sessionAPI } from '../services/api';

interface OfflineContextType {
  isOnline: boolean;
  pendingRequests: QueuedRequest[];
  addToQueue: (request: Omit<QueuedRequest, 'id' | 'timestamp' | 'status'>) => Promise<void>;
  syncPendingRequests: () => Promise<void>;
  clearQueue: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingRequests, setPendingRequests] = useState<QueuedRequest[]>([]);
  const syncingRef = useRef(false);
  const isOnlineRef = useRef(navigator.onLine);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  const loadPendingRequests = async () => {
    const requests = await db.queuedRequests.toArray();
    setPendingRequests(requests);
  };

  const addToQueue = async (request: Omit<QueuedRequest, 'id' | 'timestamp' | 'status'>) => {
    const queuedRequest: Omit<QueuedRequest, 'id'> = {
      ...request,
      timestamp: new Date(),
      status: 'pending',
    };
    
    await db.queuedRequests.add(queuedRequest);
    await loadPendingRequests();
  };

  const syncPendingRequests = useCallback(async () => {
    if (!isOnlineRef.current || syncingRef.current) return;
    
    syncingRef.current = true;
    const requests = await db.queuedRequests.where('status').equals('pending').toArray();
    
    for (const request of requests) {
      try {
        await db.queuedRequests.update(request.id!, { status: 'syncing' });
        
        const response = await api({
          method: request.method as any,
          url: request.url,
          data: request.data,
        });
        
        await db.queuedRequests.update(request.id!, { 
          status: 'synced',
          response: response.data,
        });
        
      } catch (error: any) {
        console.error('Failed to sync request:', error);
        
        const status = error.response?.status;
        // 401 is an auth issue, not a permanent data error -- mark as pending so it retries
        // after the Axios interceptor refreshes the token
        if (status === 401) {
          await db.queuedRequests.update(request.id!, { status: 'pending' });
        } else if (status && status >= 400 && status < 500) {
          await db.queuedRequests.update(request.id!, { 
            status: 'failed',
            error: error.response.data,
          });
        } else {
          await db.queuedRequests.update(request.id!, { status: 'pending' });
        }
      }
    }
    
    await loadPendingRequests();
    syncingRef.current = false;
  }, []);

  // F4: drain the offline PHOTO queue too. The 30s loop previously only synced queuedRequests
  // (JSON), so pendingMedia was a dead-letter queue and photos were lost even after coming online.
  // This is the reliable path for browsers without Background Sync (e.g. iOS Safari) and uses the
  // fixed multipart uploadMedia + the live (fresh) auth token.
  const syncPendingMedia = useCallback(async () => {
    if (!isOnlineRef.current) return;
    let items: PendingMedia[] = [];
    try {
      items = await db.pendingMedia.where('status').equals('pending').toArray();
    } catch {
      return;
    }
    for (const item of items) {
      if (item.id == null) continue;
      try {
        await db.pendingMedia.update(item.id, { status: 'uploading' });
        await sessionAPI.uploadMedia(
          item.sessionId,
          item.file,
          item.capturedAt ? new Date(item.capturedAt) : undefined
        );
        await db.pendingMedia.update(item.id, { status: 'synced' });
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 409) {
          // Duplicate (server already has this photo by sha256) — treat as delivered.
          await db.pendingMedia.update(item.id, { status: 'synced' });
        } else if (status && status >= 400 && status < 500 && status !== 401 && status !== 429) {
          await db.pendingMedia.update(item.id, { status: 'failed', retryCount: (item.retryCount || 0) + 1, lastRetry: new Date() });
        } else {
          await db.pendingMedia.update(item.id, { status: 'pending', retryCount: (item.retryCount || 0) + 1, lastRetry: new Date() });
        }
      }
    }
  }, []);

  const clearQueue = async () => {
    await db.queuedRequests.clear();
    await loadPendingRequests();
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingRequests();
      syncPendingMedia();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    loadPendingRequests();

    if (isOnlineRef.current) {
      syncPendingRequests();
      syncPendingMedia();
    }

    const syncInterval = setInterval(() => {
      if (isOnlineRef.current) {
        syncPendingRequests();
        syncPendingMedia();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(syncInterval);
    };
  }, [syncPendingRequests, syncPendingMedia]);

  const value = {
    isOnline,
    pendingRequests,
    addToQueue,
    syncPendingRequests,
    clearQueue,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};
